/**
 * W8 PR-1 — Faction-Specific Boss AI 階段差別化
 *
 * 五門派 boss 在通用 phase / berserk 之上,各自帶一個「獨特機制」。
 * 採 strategy-pattern + table:
 *   FACTION_MECHANIC[factionId] → FactionMechanicFn(boss, nowMs, state, playerX)
 * Scene 每幀在 `tickAttacks` 之後呼叫對應 mechanic,把 return 的
 * `BossAttack[]` 接續走 `_executeBossAttack` pipeline。
 *
 * 機制 (per spec docs/game-pr-specs/W8-boss-polish.md §2):
 *  - celestial (星算): 每跨越 75/50/25% HP 閾值 → fan-shot 3 道公式彈幕
 *  - runic     (符文): 每 30 s 觸發 castedShield (借 silence-player kind,
 *                       由 scene 解讀為 boss 護盾 5 s)
 *  - techno    (機械): 每 8 s 瞬移到玩家 X ±60、發 1 道 beam;phase ≥2 改 5 s
 *  - elemental (自然): 死亡後在原位再生 2 個 minion (one-shot)
 *  - dominion  (疆域): 每跨越 70/40% HP 閾值 → 移速 +20% (累加 ×2)
 *
 * 鐵則 (CLAUDE.md §學習與遊戲分離):
 *  本檔純 game-side,**不得** import 任何 src/learn/* 模組。
 */

import type { Boss, BossAttack } from './boss';
import { GAME } from '../config';

// ─── State ──────────────────────────────────────────────────────────────────

/**
 * Per-spawn mutable state shared by all 5 faction mechanics.
 *
 * Fields are intentionally generic — each faction reinterprets them so the
 * table stays one shape per entry (avoids subclass explosion).
 *
 *   celestial  →  counter = thresholds already crossed (0..3)
 *   runic      →  timerAMs = last shield cast wall-clock (ms);
 *                  flag    = shield currently active (informational only —
 *                            scene owns the real activation flag)
 *   techno     →  timerAMs = last teleport-dash wall-clock (ms)
 *   elemental  →  flag     = death-respawn already happened (one-shot guard)
 *   dominion   →  counter  = speed boosts already applied (0..2)
 */
export interface FactionMechanicState {
  /** Generic timer A (ms). -Infinity = never fired. */
  timerAMs: number;
  /** Generic timer B (ms). Reserved for future mechanics. */
  timerBMs: number;
  /** Generic integer counter / threshold-cross tally. */
  counter: number;
  /** Generic one-shot / active boolean. */
  flag: boolean;
}

/** Fresh per-spawn state. -Infinity timers ensure first-tick eligibility. */
export function makeFactionMechanicState(): FactionMechanicState {
  return { timerAMs: -Infinity, timerBMs: -Infinity, counter: 0, flag: false };
}

// ─── Strategy fn signature & table ──────────────────────────────────────────

/**
 * Per-faction tick: invoked from scene `_updateBoss` AFTER `tickAttacks`,
 * BEFORE same-frame death dispatch. Return value is appended to the same
 * `BossAttack` pipeline used by the normal A/B attacks.
 *
 * @param boss      live Boss instance (mechanic may mutate `boss.x` /
 *                  `boss.def.speedPxPerSec` — see techno / dominion).
 * @param nowMs     current wall-clock from scene `time.now`.
 * @param state     mutable per-spawn state (see {@link FactionMechanicState}).
 * @param playerX   used by aim-aware mechanics (techno teleport-toward-player).
 */
export type FactionMechanicFn = (
  boss: Boss,
  nowMs: number,
  state: FactionMechanicState,
  playerX: number,
) => BossAttack[];

// ─── Tunables (exported for test inspection) ────────────────────────────────

/** Runic shield: cast cadence (ms). */
export const RUNIC_SHIELD_INTERVAL_MS = 30_000;
/** Runic shield: each cast keeps the boss immune for this long. */
export const RUNIC_SHIELD_DURATION_MS = 5_000;
/** Techno teleport-dash cadence in phase 1 (ms). */
export const TECHNO_DASH_INTERVAL_P1_MS = 8_000;
/** Techno teleport-dash cadence in phase ≥2 (ms). */
export const TECHNO_DASH_INTERVAL_P2_MS = 5_000;
/** Dominion speed multiplier added per HP threshold crossed. */
export const DOMINION_SPEED_BONUS_PCT = 0.20;
/** Dominion: max stacked boosts. */
export const DOMINION_MAX_BOOSTS = 2;
/** Elemental: minion count spawned at boss death. */
export const ELEMENTAL_DEATH_MINIONS = 2;
/** Celestial: per-threshold fan angles (rad). */
export const CELESTIAL_FAN_ANGLES: number[] = [-0.35, 0, 0.35];
/**
 * 星算門「公式彈幕」每顆傷害。R-006(boss 2026-06-14「魔王沒一定難度前面就無聊」):
 * 原本 = 1,是全 boss 招式中的離群值(主招 8–16、tier2/3 12–22、techno beam 15)。
 * 星算門 = 第一個魔王 shu-mo,招牌特招只痛 1 點 → 更顯無聊。3 顆寬間距扇彈、整場只
 * 丟 3 次(過 75/50/25% HP),拉到 12 = 「躲開或吃 12」的真威脅,仍可閃(鐵則)。
 */
export const CELESTIAL_FAN_DAMAGE = 12;

// ─── Strategies ─────────────────────────────────────────────────────────────

/**
 * 星算門 — 公式彈幕。
 * 每跨越 75% / 50% / 25% HP 任一閾值往下穿越時拋 3 道 fan-shot。
 * 同一閾值只觸發一次 (counter 單調遞增)。
 */
function celestialMechanic(
  boss: Boss,
  _nowMs: number,
  state: FactionMechanicState,
  _playerX: number,
): BossAttack[] {
  if (state.counter >= 3) return [];
  const hpRatio = boss.hp / boss.hpMax;
  const nextThreshold = 0.75 - state.counter * 0.25;
  if (hpRatio < nextThreshold) {
    state.counter++;
    return [
      {
        kind: 'fan-shot',
        angles: [...CELESTIAL_FAN_ANGLES],
        speedPxPerSec: 320,
        damage: CELESTIAL_FAN_DAMAGE,
      },
    ];
  }
  return [];
}

/**
 * 符文門 — castedShield。
 * 每 30 s 借 `silence-player` kind 觸發 5 s 護盾。Scene 端
 * `_executeBossAttack` 對 runic-faction 來源的 silence-player 改寫語意為
 * 「boss 護盾」: 開啟 `_bossShieldActive` 旗,期間 boss takeDamage no-op。
 */
function runicMechanic(
  _boss: Boss,
  nowMs: number,
  state: FactionMechanicState,
  _playerX: number,
): BossAttack[] {
  if (nowMs - state.timerAMs < RUNIC_SHIELD_INTERVAL_MS) return [];
  state.timerAMs = nowMs;
  state.flag = true; // informational; real flag lives on scene
  return [{ kind: 'silence-player', durationMs: RUNIC_SHIELD_DURATION_MS }];
}

/**
 * 機械門 — 瞬移 dash。
 * 每 8 s (phase ≥2 縮 5 s) 直接 mutate `boss.x` 到玩家 X ±60,然後射 beam。
 * 瞬移使用 `GAME.width` clamp 邊界 [60, width-60] 避免出框。
 */
function technoMechanic(
  boss: Boss,
  nowMs: number,
  state: FactionMechanicState,
  playerX: number,
): BossAttack[] {
  const interval = boss.phase >= 2 ? TECHNO_DASH_INTERVAL_P2_MS : TECHNO_DASH_INTERVAL_P1_MS;
  if (nowMs - state.timerAMs < interval) return [];
  state.timerAMs = nowMs;
  // Teleport: ±60 jitter so kid sees side-step not stack-on-player.
  const side = Math.random() > 0.5 ? 60 : -60;
  boss.x = Math.max(60, Math.min(GAME.width - 60, playerX + side));
  // Aim straight at the player Y baseline (matches scene's vertical convention).
  const angle = Math.atan2(playerX - boss.x, GAME.height * 0.85 - boss.y);
  return [{ kind: 'beam-cast', angleRad: angle, speedPxPerSec: 450, damage: 15 }];
}

/**
 * 自然門 — 死亡再生 (placeholder fn).
 * `tickAttacks`-shaped no-op so the table entry exists; the real respawn is
 * triggered out-of-band by {@link applyElementalDeathRespawn} from
 * `_bossDefeated()` (it needs minion-spawn helpers the per-frame tick lacks).
 */
function elementalMechanic(
  _boss: Boss,
  _nowMs: number,
  _state: FactionMechanicState,
  _playerX: number,
): BossAttack[] {
  return [];
}

/**
 * 疆域門 — 移速 +20%。
 * 每跨越 70% / 40% HP 閾值,直接 mutate `boss.def.speedPxPerSec *= 1.2`。
 * 因為 BossDef 在 boss-pool / boss-tier2-pool 是 module-level const,單純
 * mutate 會污染全 process 內後續 boss spawn — 但實務上 boss 是 per-run
 * spawn 一次,scene tear-down 不會 reuse 同實例,且 spec 明確要求「下幀
 * renderer 即生效」,故維持 in-place mutate;後續需求若改為 multi-boss
 * stacking 再抽 per-instance speed override field。
 */
function dominionMechanic(
  boss: Boss,
  _nowMs: number,
  state: FactionMechanicState,
  _playerX: number,
): BossAttack[] {
  if (state.counter >= DOMINION_MAX_BOOSTS) return [];
  const hpRatio = boss.hp / boss.hpMax;
  const nextThreshold = 0.70 - state.counter * 0.30;
  if (hpRatio < nextThreshold) {
    state.counter++;
    boss.def.speedPxPerSec *= (1 + DOMINION_SPEED_BONUS_PCT);
  }
  return [];
}

/**
 * 自然門 — death-respawn helper (called from `_bossDefeated`, NOT per-frame).
 *
 * Returns 2 spawn positions ±40 px around the boss death point on the first
 * call; idempotent on subsequent calls (one-shot via `state.flag`).
 *
 * Scene then loops the result and calls `_spawnEnemyAt(x, y, 'basic')`
 * (delegated to a tiny shim — `_spawnBossMinionAt` — wired in W8 PR-1).
 *
 * Faction guard: even if scene mis-routes a non-elemental boss here, the
 * helper bails so we never spawn surprise re-gen for other factions.
 */
export interface ElementalDeathRespawnResult {
  shouldSpawnMinionAt: Array<{ x: number; y: number }>;
}
export function applyElementalDeathRespawn(
  boss: Boss,
  state: FactionMechanicState,
): ElementalDeathRespawnResult {
  if (boss.def.faction !== 'elemental' || state.flag) {
    return { shouldSpawnMinionAt: [] };
  }
  state.flag = true;
  return {
    shouldSpawnMinionAt: [
      { x: boss.x - 40, y: boss.y },
      { x: boss.x + 40, y: boss.y },
    ],
  };
}

/**
 * Faction → mechanic table. Keyed by `BossDef.faction` (string union, kept
 * lower-case to match the existing pool defs). Missing entry = boss runs
 * with stock A/B attacks only (graceful no-op for future factions / mixed
 * tier-2 hybrids that don't get a unique mechanic).
 */
export const FACTION_MECHANIC: Partial<Record<string, FactionMechanicFn>> = {
  celestial: celestialMechanic,
  runic:     runicMechanic,
  techno:    technoMechanic,
  elemental: elementalMechanic,
  dominion:  dominionMechanic,
};
